// src/admin/shell/useTweaks.ts
// Holds the user's theme + density preferences. We use a simple
// localStorage-backed store with a custom event so multiple components stay
// in sync (AppShell needs the values to set classes; TweaksPanel reads &
// writes the same values). No context provider needed for two scalar values.
import { useEffect, useState } from 'react';

export type ThemeVariant = 'classic' | 'ink' | 'daylight';
export type Density = 'compact' | 'comfortable' | 'relaxed';

const KEY = 'thandizo.admin.tweaks';
const EVT = 'thandizo:tweaks-changed';

interface Tweaks {
  variant: ThemeVariant;
  density: Density;
}

const DEFAULTS: Tweaks = { variant: 'classic', density: 'comfortable' };

function read(): Tweaks {
  if (typeof window === 'undefined') return DEFAULTS;
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw) as Partial<Tweaks>;
    return {
      variant: parsed.variant === 'ink' || parsed.variant === 'daylight' ? parsed.variant : 'classic',
      density: parsed.density === 'compact' || parsed.density === 'relaxed' ? parsed.density : 'comfortable',
    };
  } catch {
    return DEFAULTS;
  }
}

function write(t: Tweaks): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(KEY, JSON.stringify(t));
  window.dispatchEvent(new CustomEvent<Tweaks>(EVT, { detail: t }));
}

export function useTweaks() {
  const [tweaks, setTweaks] = useState<Tweaks>(read);

  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<Tweaks>).detail;
      if (detail) setTweaks(detail);
    };
    window.addEventListener(EVT, onChange);
    return () => window.removeEventListener(EVT, onChange);
  }, []);

  return {
    variant: tweaks.variant,
    density: tweaks.density,
    setVariant: (v: ThemeVariant) => write({ ...tweaks, variant: v }),
    setDensity: (d: Density) => write({ ...tweaks, density: d }),
  };
}
